import { Calendar, SettingsSort } from '@griply/assets';
import { FilterDocument, GroupByMode } from '@griply/types';
import {
  buildTodayDefaultFilter,
  mapPartialToTodaySettings,
  todayFilters,
  todayGroupOptions,
  todaySortOptions,
} from '@griply/utils';
import Typography from '@mui/material/Typography';
import { useGoals } from 'features/goals/hooks/use-goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useSortedTags } from 'features/tags';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { MobileTodoNavigation } from 'shared/components/ui/mobile-todo-navigation';
import { useOpenCreateTask } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes/paths';
import { useTheme } from 'styled-components';

import { useTodayFilterDocument } from '../../hooks/use-today-filter-document';
import { MainContextMenu } from './main-context-menu';
import * as Styled from './page-top-bar.style';
import { SearchButton } from './search-button';
import { UserAvatar } from './user-avatar';

export type PageTopBarProps = {
  onCalendar?: () => void;
  calendarOpen?: boolean;
  totalDurationLabel?: string;
};

export const PageTopBar: React.FC<PageTopBarProps> = ({
  onCalendar,
  calendarOpen,
  totalDurationLabel,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const activePath = useActivePath({ level: 2 });
  const updateUserSettings = useUpdateUserSettings();
  const today = useToday();

  const tags = useSortedTags();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const filterDocument = useTodayFilterDocument();
  const openCreateTask = useOpenCreateTask();

  const defaultFilterDocument = useMemo(
    () => buildTodayDefaultFilter(today),
    [today],
  );

  const onCreate = useCallback(
    () =>
      openCreateTask({
        endStrategy: { deadline: new Date(), completionCount: null },
      }),
    [openCreateTask],
  );

  const updateFilterDocument = useCallback(
    (newFilterDocument: FilterDocument) => {
      const settings = mapPartialToTodaySettings(newFilterDocument);
      if (settings) {
        updateUserSettings(settings);
      }
    },
    [updateUserSettings],
  );

  const onReset = useCallback(() => {
    updateUserSettings({
      filters: {
        today: defaultFilterDocument,
      },
    });
  }, [defaultFilterDocument, updateUserSettings]);

  return (
    <TopBarBase
      mobileNavigationSlot={
        <MobileTodoNavigation activePath={activePath as Paths} />
      }
    >
      <Styled.Container>
        <Styled.Title>
          <Typography variant="h5" component="h1">
            {t('pages.today.title')}
            {totalDurationLabel && (
              <Styled.Duration>{totalDurationLabel}</Styled.Duration>
            )}
          </Typography>
        </Styled.Title>

        <Styled.Interactions>
          {theme.isMobile && <SearchButton />}

          <FilterButton
            filterDocument={filterDocument}
            defaultFilterDocument={defaultFilterDocument}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={updateFilterDocument}
            onReset={onReset}
            supportedFilters={todayFilters}
            supportedGroupByModes={todayGroupOptions}
            supportedSortingModes={todaySortOptions}
            labelOverrides={{
              [GroupByMode.Deadline]: t(
                `context-menus.filtering.task.grouping-and-sorting.grouping.options.${GroupByMode.None}`,
              ),
            }}
            renderAsIconButton
            preventDrag
          />
          {!theme.isMobile && (
            <IconButton
              ref={buttonRef}
              icon={SettingsSort}
              strokeWidth={2}
              onClick={openMenu}
              color={Colors.Default}
              highlighted={menuOpen}
              preventDrag
            />
          )}
          {menuOpen && (
            <MainContextMenu onClose={closeMenu} referenceElement={buttonRef} />
          )}
          {!theme.isMobile && <Styled.Divider />}
          {!theme.isMobile && (
            <IconButton
              icon={Calendar}
              strokeWidth={2}
              onClick={onCalendar}
              highlighted={calendarOpen}
              color={Colors.Default}
              preventDrag
            />
          )}
          {theme.isMobile && <UserAvatar />}
        </Styled.Interactions>
      </Styled.Container>
    </TopBarBase>
  );
};
